'use client';

import { useState, useEffect } from 'react';
import config from '../../config/config';
import UnifiedPostLayout from './UnifiedPostLayout';
import StandardPostGrid from './StandardPostGrid';
import CategoryList from './CategoryList';
import PostsSkeleton from './PostsSkeleton';
import Link from 'next/link';
import { Button } from '../ui/button';
import { Skeleton } from "../ui/skeleton";

export default function StandardPostPage({ title, description, postType, apiPath }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const fetchPosts = async (categorySlug = null) => {
    try {
      setLoading(true);
      // Build URL with or without category filter
      let url = `${config.NEXT_PUBLIC_API_URL}/api/public/p/${apiPath || postType}?limit=24`;
      
      if (categorySlug) {
        url = `${config.NEXT_PUBLIC_API_URL}/api/public/posts?type=${postType}&category=${categorySlug}&limit=24`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch ${postType} posts`);
      }

      const result = await response.json();
      
      if (result.success) {
        setPosts(result.data.posts);
      } else {
        throw new Error(result.message || `Failed to fetch ${postType} posts`);
      }
    } catch (err) {
      console.error(`Error fetching ${postType} posts:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial load of posts
  useEffect(() => {
    fetchPosts();
  }, [postType, apiPath]);

  const handleCategoryChange = (categorySlug) => {
    setSelectedCategory(categorySlug);
    fetchPosts(categorySlug);
    
    // Scroll to the top of the posts section
    window.scrollTo({
      top: document.getElementById('posts-section')?.offsetTop - 100 || 0,
      behavior: 'smooth'
    });
  };

  if (loading && posts.length === 0) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="container mx-auto">
          <Skeleton className="h-10 w-1/3 mb-8" />
          <div className="mb-8">
            <div className="flex overflow-x-auto no-scrollbar py-1">
              <div className="flex space-x-10 px-1 pb-1">
                <Skeleton className="h-9 w-16" />
                {[1, 2, 3, 4, 5].map((item) => (
                  <Skeleton key={item} className="h-9 w-28" />
                ))}
              </div>
            </div>
          </div>
          <PostsSkeleton />
        </div>
      </div>
    );
  }

  if (error && posts.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500">Error: {error}</p>
          <Link href="/" className="inline-block mt-4">
            <Button>Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Create a mock post for the page layout
  const pagePost = {
    title: title,
    type: 'page',
  };

  return (
    <UnifiedPostLayout post={pagePost}>
      <div id="posts-section">
        <CategoryList 
          postType={postType} 
          selectedCategory={selectedCategory} 
          onCategoryChange={handleCategoryChange}
          loading={loading}
        />
        
        {loading && (
          <PostsSkeleton />
        )}
        
        {!loading && posts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No posts found in this category.</p>
            <button 
              onClick={() => handleCategoryChange(null)} 
              className="mt-4 text-primary hover:underline"
            >
              View all posts
            </button>
          </div>
        )}
        
        {!loading && posts.length > 0 && (
          <StandardPostGrid posts={posts} postType={postType} />
        )}
      </div>
    </UnifiedPostLayout>
  );
}
